// Streak badge — extrinsic + balanced learners only. Intrinsic users
// get null. Visual intensity scales with the visibility flag: extrinsic
// gets the gold-flame treatment; balanced gets a quiet outlined pill.

import { useEffect, useState } from 'react';
import { Flame } from 'lucide-react';
import { useMotivationalDriverVisibility } from '@shared/hooks/useMotivationalDriverVisibility';
import {
  subscribeGamificationSummary,
  ensureGamificationDoc,
} from '@shared/services/gamification';
import type { UserGamification } from '@shared/types/gamification';

interface StreakBadgeProps {
  userId: string;
}

export function StreakBadge({ userId }: StreakBadgeProps) {
  const visibility = useMotivationalDriverVisibility(userId);
  const [summary, setSummary] = useState<UserGamification | null>(null);

  useEffect(() => {
    if (visibility.intrinsic) return;
    let active = true;
    void ensureGamificationDoc(userId);
    const unsub = subscribeGamificationSummary(userId, (s) => {
      if (active) setSummary(s);
    });
    return () => {
      active = false;
      unsub();
    };
  }, [userId, visibility.intrinsic]);

  if (visibility.intrinsic) return null;
  const streak = summary?.currentStreakDays ?? 0;
  if (streak === 0) return null;

  const intense = visibility.extrinsic;
  const tone = intense ? '#F0A500' : '#A39BE0';
  const bg = intense ? 'rgba(240, 165, 0, 0.16)' : 'rgba(108, 92, 231, 0.12)';

  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-pill border"
      style={{ borderColor: `${tone}40`, background: bg }}
      aria-label={`${streak} day streak`}
    >
      <Flame size={14} style={{ color: tone }} />
      <span className="font-heading text-sm font-bold" style={{ color: tone }}>
        {streak}
      </span>
      <span className="text-xs font-body text-content-muted">
        day{streak === 1 ? '' : 's'}
      </span>
    </div>
  );
}
